<template>
  <div class="report-page">
    <div class="report-header">
      <h1><el-icon><PictureFilled /></el-icon> 信息收集</h1>
      <p class="subtitle">拍照或录像，快速上传记录</p>
    </div>

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      class="report-form"
      @submit.prevent="handleSubmit"
    >
      <el-form-item label="现场照片/视频" prop="images">
        <div class="media-tabs">
          <el-radio-group v-model="mediaMode" size="default">
            <el-radio-button label="image">
              <el-icon><CameraFilled /></el-icon> 照片 {{ imageFiles.length ? `(${imageFiles.length})` : '' }}
            </el-radio-button>
            <el-radio-button label="video">
              <el-icon><VideoPlay /></el-icon> 视频 {{ videoItems.length ? `(${videoItems.length})` : '' }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="mediaMode === 'image' && imageFiles.length < 9" class="media-add-full" @click="triggerImageUpload">
          <el-icon :size="18"><Plus /></el-icon>
          <span>添加照片</span>
        </div>
        <div v-if="mediaMode === 'image' && imagePreviews.length" class="media-grid">
          <div v-for="(url, idx) in imagePreviews" :key="idx" class="media-item">
            <img :src="url" alt="照片" />
            <button class="media-remove" type="button" @click="removeImage(idx)">×</button>
            <span class="media-index">{{ idx + 1 }}</span>
          </div>
        </div>

        <div v-if="mediaMode === 'video' && videoItems.length < 3" class="media-add-full" @click="triggerVideoUpload">
          <el-icon :size="18"><Plus /></el-icon>
          <span>添加视频</span>
        </div>
        <div v-if="mediaMode === 'video' && videoItems.length" class="media-grid">
          <div v-for="(v, idx) in videoItems" :key="idx" class="media-item media-video">
            <video :src="v.url" muted playsinline></video>
            <button class="media-remove" type="button" @click="removeVideo(idx)">×</button>
            <span class="media-index">{{ idx + 1 }}</span>
          </div>
        </div>

        <div class="upload-hint">{{ mediaMode === 'image' ? '最多 9 张，单张不超过 20MB（JPG/PNG/GIF/WebP）' : '最多 3 段，单段不超过 100MB（MP4/MOV/WebM）' }}</div>
        <input ref="imageInputRef" type="file" accept="image/*" multiple capture="environment" style="display:none" @change="onImagesChange" />
        <input ref="videoInputRef" type="file" accept="video/*" multiple capture="environment" style="display:none" @change="onVideosChange" />
      </el-form-item>

      <el-form-item label="备注（选填）" prop="description">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          placeholder="简要说明（选填）"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="上传人（选填）" prop="reporter">
        <el-input v-model="form.reporter" placeholder="姓名或工号" maxlength="30" />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" class="submit-btn" :loading="submitting" @click="handleSubmit">
          <el-icon><Upload /></el-icon> 提交上传
        </el-button>
      </el-form-item>
    </el-form>

    <!-- 成功对话框 -->
    <el-dialog v-model="successDialogVisible" title="上传成功" width="90%" :close-on-click-modal="false" class="success-dialog">
      <div class="success-body">
        <el-icon :size="56" color="#67c23a"><CircleCheckFilled /></el-icon>
        <p class="success-title">记录已上传</p>
        <p class="success-label">唯一记录码</p>
        <p class="success-id">{{ submittedId }}</p>
        <p class="success-tip">请妥善保存记录码，可用于后续查询和核对</p>
      </div>
      <template #footer>
        <el-button type="primary" @click="resetForm">继续上传</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { submitHazard } from '../api'

const formRef = ref(null)
const imageInputRef = ref(null)
const videoInputRef = ref(null)
const submitting = ref(false)
const successDialogVisible = ref(false)
const submittedId = ref('')

const imageFiles = ref([])
const imagePreviews = ref([])
const videoItems = ref([])
const mediaMode = ref('image')

const form = reactive({
  description: '',
  reporter: '',
  images: []
})

const rules = {
  images: [{
    validator: (rule, value, callback) => {
      if (imageFiles.value.length === 0 && videoItems.value.length === 0) {
        callback(new Error('请至少上传一张照片或一段视频'))
      } else {
        callback()
      }
    },
    trigger: 'change'
  }]
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

function triggerImageUpload() { imageInputRef.value?.click() }
function triggerVideoUpload() { videoInputRef.value?.click() }

function onImagesChange(event) {
  const files = Array.from(event.target.files || [])
  for (const file of files) {
    if (imageFiles.value.length >= 9) { ElMessage.warning('最多上传 9 张照片'); break }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) { ElMessage.error('仅支持 JPG/PNG/GIF/WebP 格式'); continue }
    if (file.size > 20 * 1024 * 1024) { ElMessage.error('单张图片不能超过 20MB'); continue }
    imageFiles.value.push(file)
    const reader = new FileReader()
    reader.onload = (e) => { imagePreviews.value.push(e.target.result) }
    reader.readAsDataURL(file)
  }
  event.target.value = ''
}

function removeImage(idx) {
  imageFiles.value.splice(idx, 1)
  imagePreviews.value.splice(idx, 1)
}

function onVideosChange(event) {
  const files = Array.from(event.target.files || [])
  for (const file of files) {
    if (videoItems.value.length >= 3) { ElMessage.warning('最多上传 3 段视频'); break }
    if (file.size > 100 * 1024 * 1024) { ElMessage.error('单段视频不能超过 100MB'); continue }
    videoItems.value.push({ file, url: URL.createObjectURL(file) })
  }
  event.target.value = ''
}

function removeVideo(idx) {
  const item = videoItems.value[idx]
  if (item) URL.revokeObjectURL(item.url)
  videoItems.value.splice(idx, 1)
}

async function handleSubmit() {
  if (!formRef.value) return
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    ElMessage.warning('请至少上传一张照片或一段视频')
    return
  }
  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('description', form.description || '')
    formData.append('reporter', form.reporter || '')
    imageFiles.value.forEach(f => formData.append('images', f))
    videoItems.value.forEach(v => formData.append('videos', v.file))

    const result = await submitHazard(formData)
    submittedId.value = result.id
    successDialogVisible.value = true
    ElMessage.success('上传成功！')
  } catch (error) {
    ElMessage.error(error.message || '提交失败，请重试')
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  successDialogVisible.value = false
  submittedId.value = ''
  form.description = ''
  form.reporter = ''
  imageFiles.value = []
  imagePreviews.value = []
  videoItems.value.forEach(v => URL.revokeObjectURL(v.url))
  videoItems.value = []
  mediaMode.value = 'image'
  formRef.value?.resetFields()
  if (imageInputRef.value) imageInputRef.value.value = ''
  if (videoInputRef.value) videoInputRef.value.value = ''
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0;
}

.report-header {
  text-align: center;
  padding: 40px 20px 20px;
  color: #fff;
}

.report-header h1 {
  font-size: 24px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.subtitle {
  margin-top: 8px;
  font-size: 14px;
  opacity: 0.85;
}

.report-form {
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 24px 20px;
  margin-top: 16px;
}

.upload-hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 6px;
}

.media-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}

.media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.media-item {
  position: relative;
  width: calc(33.333% - 7px);
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
}

.media-item img,
.media-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-video video { background: #000; }

.media-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.media-index {
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 8px;
  z-index: 1;
}

.media-add-full {
  width: 100%;
  height: 48px;
  border: 2px dashed #dcdfe6;
  border-radius: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
  font-size: 15px;
}

.media-add-full:hover {
  border-color: #409eff;
  color: #409eff;
  background: #f0f7ff;
}

.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
}

.success-body {
  text-align: center;
  padding: 20px 0;
}

.success-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 12px 0 8px;
}

.success-label {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.success-id {
  font-size: 24px;
  font-weight: 700;
  color: #409eff;
  letter-spacing: 2px;
  margin: 8px 0;
  font-family: 'Courier New', monospace;
}

.success-tip {
  font-size: 12px;
  color: #c0c4cc;
  margin: 0;
}
</style>
